import type { Message } from '../api/types';

/**
 * Reading the WhatsApp-specific parts of a message. `contentAttributes.wa` is where
 * the adapter records what kind of thing arrived, and these helpers are shared by the
 * bubble parts and by the bubble itself, which has to know when a card already says
 * everything the plain `content` says.
 */
export interface WaLocation {
  latitude?: number;
  longitude?: number;
  name?: string;
  address?: string;
  url?: string;
}

export interface WaAttributes {
  echo?: boolean;
  unsupported?: boolean;
  error?: string;
  flowResponse?: { name?: string; body?: string; response?: unknown };
  referral?: { source_type?: string; headline?: string; body?: string; source_url?: string };
}

export const waOf = (message: Message): WaAttributes =>
  (message.contentAttributes.wa as WaAttributes | undefined) ?? {};

export const locationOf = (message: Message) => {
  const location = message.contentAttributes.location as WaLocation | undefined;
  return location && location.latitude !== undefined ? location : null;
};

export const flowRowsOf = (message: Message): [string, unknown][] => {
  const answers = waOf(message).flowResponse?.response;
  if (!answers || typeof answers !== 'object') return [];
  return Object.entries(answers as Record<string, unknown>).filter(
    ([, value]) => value !== null && value !== undefined && value !== '',
  );
};

/**
 * Whether a card below already says everything `content` says. The server writes a
 * flat text rendering of these messages for previews and notifications, so showing
 * both prints the address twice and the shared name twice.
 */
export const waRendersBody = (message: Message): boolean =>
  !!waOf(message).unsupported ||
  !!locationOf(message) ||
  flowRowsOf(message).length > 0 ||
  message.attachments.some((attachment) => attachment.fileType === 'contact');
